import React, { FC, useState } from "react";
import { Box, Collapse, IconButton, Paper, Typography } from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { SILENT_MIN, STALE_MIN } from "map/liveBoard";
import { BOARD_COLORS, LEG_COLOR, OVERLAY_Z } from "./palette";
import LegGlyph, { Mark, MarkKind } from "./LegGlyph";

const shapeRows: { kind: MarkKind; label: string }[] = [
  { kind: "vehicle", label: "Vehicle" },
  { kind: "pickup", label: "Pickup point" },
  { kind: "dropoff", label: "Dropoff point" },
  { kind: "hub", label: "Hub" },
];

/**
 * The two rows that are not one of the four marks.
 *
 * Both are on the map and both were in the standalone board's legend; they were dropped in the
 * port. The dashed line is the more important of the two -- it is drawn between every van and
 * its next stop, so leaving it out meant the legend did not explain something the map shows
 * everywhere.
 */
const legLineSwatch = (
  <svg width={18} height={18} viewBox="0 0 18 18">
    <path d="M2 9 H16" stroke="currentColor" strokeWidth={1.5} strokeDasharray="4 3" />
  </svg>
);

const unitLabelSwatch = (
  <svg width={18} height={18} viewBox="0 0 18 18">
    <circle cx={5} cy={9} r={4} fill="currentColor" stroke={BOARD_COLORS.ring} strokeWidth={1.5} />
    <text x={11} y={12} fontFamily="IBM Plex Mono, monospace" fontSize={8} fill="currentColor">
      52
    </text>
  </svg>
);

const legRows = [
  { leg: "pending", label: "Pending (at pickup)" },
  { leg: "to_pickup", label: "To pickup" },
  { leg: "to_dropoff", label: "To dropoff" },
  { leg: "return", label: "Returning to hub" },
];

const Row: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, whiteSpace: "nowrap" }}>{children}</Box>
);

const ringSwatch = (color: string, dashed: boolean) => (
  <svg width={18} height={18} viewBox="0 0 18 18">
    <circle cx={9} cy={9} r={7.5} fill="none" stroke={color} strokeWidth={1.6} strokeDasharray={dashed ? "2 2" : null} />
    {dashed ? <circle cx={9} cy={9} r={4} fill={color} stroke={BOARD_COLORS.ring} strokeWidth={1.5} /> : null}
  </svg>
);

/**
 * The legend strip over the map.
 *
 * Collapsible because on a phone it would otherwise cover the map it is explaining, and because
 * the people who read this board every day stop needing it after a week.
 */
const MapLegend: FC = () => {
  const [open, setOpen] = useState(true);

  return (
    <Paper
      elevation={3}
      sx={{
        position: "absolute",
        left: 8,
        bottom: 8,
        // Trip Markers carry their own z-index, so without one the legend loses the stack and the
        // marks draw through the panel explaining them.
        zIndex: OVERLAY_Z,
        maxWidth: { xs: "calc(100% - 56px)", md: 620 },
        px: 1,
        py: 0.5,
        fontSize: 11,
        opacity: 0.96,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
          Legend
        </Typography>
        <IconButton size="small" onClick={() => setOpen(!open)} aria-label={open ? "Hide legend" : "Show legend"}>
          {open ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={open}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, alignItems: "center", pb: 0.5 }}>
          {shapeRows.map((s) => (
            <Row key={s.kind}>
              <svg width={18} height={18} viewBox="-9 -9 18 18" style={{ color: BOARD_COLORS.hub }}>
                <Mark kind={s.kind} x={0} y={0} r={5} />
              </svg>
              {s.label}
            </Row>
          ))}
          <Row>
            <Box component="span" sx={{ color: BOARD_COLORS.hub, display: "flex" }}>
              {legLineSwatch}
            </Box>
            Vehicle → next stop
          </Row>
          <Row>
            <Box component="span" sx={{ color: BOARD_COLORS.hub, display: "flex" }}>
              {unitLabelSwatch}
            </Box>
            Unit / trip number
          </Row>
          <Box sx={{ width: "100%" }} />
          {legRows.map((l) => (
            <Row key={l.leg}>
              <LegGlyph leg={l.leg} color={LEG_COLOR[l.leg]} />
              {l.label}
            </Row>
          ))}
          <Box sx={{ width: "100%" }} />
          <Row>
            {ringSwatch(BOARD_COLORS.late, true)}
            Late
          </Row>
          <Row>
            {ringSwatch(BOARD_COLORS.warn, true)}
            <span>
              Warning
              <Box component="span" sx={{ color: "text.secondary" }}>
                {` · pot. late · no driver/vehicle · GPS > ${STALE_MIN}m · silent ${SILENT_MIN}m`}
              </Box>
            </span>
          </Row>
          <Row>
            {ringSwatch(BOARD_COLORS.backhaul, false)}
            Backhaul (when highlighted)
          </Row>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default MapLegend;
